import React from 'react';
import PropTypes from 'prop-types';
import './styles.module.scss';

export interface TableElement {
  value: string;
  colspan?: number;
  rowspan?: number;
}

/**
 * Render and style groups of rows for PDP Table
 */
const TableRowGroup = ({
  rows,
  rowStyle,
  isRowInHeader,
  hasDarkenedFirstColumn,
  firstColumnColspan,
  cellWidth,
}: {
  rows: TableElement[][];
  rowStyle?: string;
  isRowInHeader?: boolean;
  hasDarkenedFirstColumn?: boolean;
  firstColumnColspan: number;
  cellWidth: string;
}) => {
  const isMultiCol = (rowIndex: number, columnIndex: number) => {
    // Determines if a table cell (`<td>`) should have special styling given:
    //   - whether the table's first header cell spans multiple columns (`firstColumnColspan`)
    //   - the cell's position (`rowIndex` and `columnIndex`) relative to `firstColumnColspan`
    if (firstColumnColspan > 1) {
      return (
        (rowIndex === 0 && columnIndex < firstColumnColspan) ||
        columnIndex < firstColumnColspan - 1 // assumes first row in group's first cell spans multiple rows
      );
    }

    return false;
  };

  return (
    <>
      {rows.map((row, rowIndex) => {
        return (
          /* TODO - when we move forward with permanent & scalable implementation
           * use a better source for the key */
          /* eslint-disable-next-line react/no-array-index-key */
          <tr key={rowIndex} styleName={rowStyle}>
            {row.map((element, index) => {
              return isRowInHeader ? (
                <th
                  /* eslint-disable-next-line react/no-array-index-key */
                  key={index}
                  styleName="th"
                  colSpan={element.colspan || 1}
                  rowSpan={element.rowspan || 1}
                >
                  {element.value}
                </th>
              ) : (
                <td
                  /* eslint-disable-next-line react/no-array-index-key */
                  key={index}
                  // using index 0 of each row to determine the first column
                  styleName={`td ${cellWidth} ${
                    hasDarkenedFirstColumn && index === 0 ? 'dark' : ''
                  } ${isMultiCol(rowIndex, index) ? 'multi-colspan' : ''}`}
                  colSpan={element.colspan || 1}
                  rowSpan={element.rowspan || 1}
                >
                  {element.value}
                </td>
              );
            })}
          </tr>
        );
      })}
    </>
  );
};

TableRowGroup.defaultProps = {
  rowStyle: 'trDefault',
  isRowInHeader: false,
  hasDarkenedFirstColumn: false,
  firstColumnColspan: 1,
  cellWidth: 'small',
};

TableRowGroup.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        colspan: PropTypes.number,
        rowspan: PropTypes.number,
      }),
    ),
  ).isRequired,
  rowStyle: PropTypes.string,
  isRowInHeader: PropTypes.bool,
  hasDarkenedFirstColumn: PropTypes.bool,
  firstColumnColspan: PropTypes.number,
  cellWidth: PropTypes.string,
};

export default TableRowGroup;
